// The Live view and the arbiter's broadcast panel, fetched when they are needed.
//
// js/ui/live.js and js/ui/broadcast-arbiter.js bring chess.js, the board, the
// Stockfish wrapper and the DGT bridge with them — 14 modules and some 300 KB of
// source — while most visits never broadcast anything. So app.js talks to this
// module instead, which loads the pair the first time either is needed:
//
//   - the active tournament is broadcasting. The feed listener, and the results
//     the arbiter enters flowing to a DGT bridge, run whichever tab is open, so
//     they start as soon as such a tournament is opened; or
//   - the Live view is shown. It is also where an arbiter switches broadcasting
//     ON, so it has to work before there is anything to watch.
//
// Until then there is nothing for renderLive() or resetLive() to do: nothing is
// subscribed, no board is drawn and no bridge can be running.
import { state } from '../state.js';

let live = null;      // both modules, once loaded and initialised
let loading = null;   // the load in flight

function liveView() {
    return document.getElementById('live');
}

function liveViewShown() {
    const view = liveView();
    return !!(view && view.classList.contains('active'));
}

function broadcasting() {
    const t = state.activeTournament;
    return !!(t && t.broadcast && t.broadcast.enabled);
}

function load() {
    if (!loading) {
        loading = Promise.all([import('./live.js'), import('./broadcast-arbiter.js')])
            .then(([liveModule, arbiterModule]) => {
                // The order app.js used when these were static imports.
                liveModule.initLive();
                arbiterModule.initBroadcastArbiter();
                live = { ...liveModule, ...arbiterModule };
                // Catch up with whatever changed while the code was on its way:
                // the tournament may be a different one by now, or none.
                live.renderLive();
            })
            .catch(err => {
                loading = null; // the next render or the next visit to the view retries
                console.error('Could not load the Live view:', err);
            });
    }
    return loading;
}

/** Wires the loader once, at startup. */
export function initLive() {
    const view = liveView();
    if (!view) return;
    // Showing a view is a class change on its div, wherever the navigation came
    // from — the same signal live.js itself watches once it is here.
    const observer = new MutationObserver(() => {
        if (live) observer.disconnect();
        else if (liveViewShown()) load();
    });
    observer.observe(view, { attributes: true, attributeFilter: ['class'] });
    if (liveViewShown()) load();
}

/**
 * Keeps the broadcast listener pointed at the active tournament and refreshes the
 * view and the arbiter's panel. Called on every UI update; loads the modules the
 * first time they have anything to do.
 */
export function renderLive() {
    if (live) live.renderLive();
    else if (broadcasting() || liveViewShown()) load();
}

/**
 * Drops the listener, the boards, the engine and the DGT bridge. Called when the
 * watched tournament changes and when watch mode is left.
 */
export function resetLive() {
    if (!live) return;
    live.resetLive();
    live.resetBroadcastArbiter();
}
